import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, AlertCircle } from "lucide-react";
import SEO from "../../components/site/SEO";
import { api } from "../../lib/apiClient";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 20;  // ~40s

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment: null, error: "" });
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState({ status: "error", payment: null, error: "Missing session_id in URL." });
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState({ status: "paid", payment: data, error: "" });
        return;
      }
      if (["failed", "expired", "refunded"].includes(data.payment_status)) {
        setState({ status: "failed", payment: data, error: `Payment status: ${data.payment_status}` });
        return;
      }
      setAttempts(a => a + 1);
    } catch (e) {
      setState({ status: "error", payment: null, error: e.response?.data?.detail || "Payment lookup failed." });
    }
  }, [sessionId]);

  useEffect(() => { poll(); }, [poll]);
  useEffect(() => {
    if (state.status !== "polling") return;
    if (attempts >= POLL_MAX_ATTEMPTS) {
      setState({ status: "timeout", payment: null, error: "Payment is taking longer than expected. Please refresh in a moment." });
      return;
    }
    const t = setTimeout(poll, POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [attempts, state.status, poll]);

  return (
    <>
      <SEO title="Booking confirmed — Wonder Washers" description="Your Wonder Washers appointment is confirmed." />
      <section className="max-w-2xl mx-auto py-24 px-6" data-testid="payment-success-page">
        <div className="ww-card-lg p-10 md:p-12 text-center">
          {state.status === "polling" && (
            <>
              <Loader2 className="mx-auto h-12 w-12 text-[color:var(--ww-pressure-blue)] animate-spin mb-6" />
              <h1 className="font-display uppercase text-4xl md:text-5xl text-[color:var(--ww-navy)] leading-none">Confirming your payment</h1>
              <p className="mt-4 text-slate-600">Hang tight — this usually takes a couple of seconds.</p>
            </>
          )}
          {state.status === "paid" && (
            <div data-testid="payment-success-confirmed">
              <div className="mx-auto h-14 w-14 rounded-full bg-[color:var(--ww-red)] text-white flex items-center justify-center mb-6">
                <Check className="h-7 w-7" />
              </div>
              <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-red)] uppercase mb-3">Deposit received</div>
              <h1 className="font-display uppercase text-4xl md:text-5xl text-[color:var(--ww-navy)] leading-none">You're on the schedule.</h1>
              <p className="mt-6 text-slate-600 leading-relaxed">
                We'll send a confirmation email and a reminder the day before. The remaining 50% balance is due after we finish the job — we'll send a payment link.
              </p>
              <div className="mt-8 rounded-lg bg-slate-50 border border-slate-200 p-4 text-left inline-block">
                <div className="text-xs font-subhead uppercase tracking-widest text-slate-500">Deposit paid</div>
                <div className="font-display text-3xl text-[color:var(--ww-navy)] mt-1">${(state.payment.amount_cents / 100).toFixed(2)}</div>
              </div>
              <div className="mt-10">
                <Link to="/" className="ww-btn-primary inline-block">Back home</Link>
              </div>
            </div>
          )}
          {(state.status === "failed" || state.status === "error" || state.status === "timeout") && (
            <div data-testid="payment-success-error">
              <AlertCircle className="mx-auto h-12 w-12 text-amber-500 mb-6" />
              <h1 className="font-display uppercase text-4xl md:text-5xl text-[color:var(--ww-navy)] leading-none">Hmm — payment not confirmed</h1>
              <p className="mt-4 text-slate-600 leading-relaxed">{state.error || "We couldn't confirm your payment yet. Please try again."}</p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <Link to="/book" className="ww-btn-primary">Try booking again</Link>
                <Link to="/contact" className="ww-btn-outline">Contact us</Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
